import React, { ReactNode, Suspense } from "react";
import { DefaultLayout } from "../layout/DefaultLayout";
import { UploadImageRestaurant } from "../pages/restaurant-page/create-restaurant/UploadImageRestaurant";
import CreateRestaurant from "../pages/restaurant-page/create-restaurant/CreateRestaurant";
import Plans from "../pages/restaurant-page/create-restaurant/Plans";
import Success from "../pages/restaurant-page/success/Success";
import Cancel from "../pages/restaurant-page/cancel/Cancel";
import Refresh from "src/pages/restaurant-page/refresh/Refresh";
import Return from "src/pages/restaurant-page/return/Return";
import FinixOnboardingRefresh from "src/pages/restaurant-page/finix/FinixOnboardingRefresh";
import FinixOnboardingReturn from "src/pages/restaurant-page/finix/FinixOnboardingReturn";
import QrCodeModal from "src/components/QrCodeModal/QrCodeModal";
import DeleteRestaurant from "src/components/DeleteRestaurant/DeleteRestaurant";
import ShowTokenRefreshModal from "src/components/ShowTokenRefreshModal/ShowTokenRefreshModal";
import { RestaurantPage } from "../pages/restaurant-page/RestaurantPage";
import RestaurantSetting from "../pages/Restaurant-setting/RestaurantSetting";
import Employee from "src/pages/Employee/Employee";
import Promotions from "src/pages/PromotionManagement/Promotions";
import InviteEmployee from "../components/InviteEmployeeModal/InviteEmployeeModal";
import MenuCategoriesPage from "src/pages/MenuCategoriesPage";
import UserProfileAccount from "src/pages/UserProfileAccount/UserProfileAccount";
import SubcriptionSetting from "src/components/SubcriptionSetting/SubcriptionSetting";
import Combo from "src/pages/Combo/Combo";
import RestaurantTable from "src/pages/RestaurantTable/RestaurantTable";
import CustomersPage from "src/pages/CustomersPage";
import UpdateCombo from "src/pages/Combo/UpdateCombo";
import OrdersPage from "src/pages/OrdersPage";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { parseRestaurantSegment, restaurantSegment } from "src/utils/restaurantUrl";
import ModifierItemsPage from "src/pages/ModifierItemsPage";
import ModifierCategoriesPage from "src/pages/ModifierCategoriesPage";
import EventsPage from "src/pages/EventsPage";
import { RequireFeature } from "src/components/RequireFeature/RequireFeature";
import { FEATURE_NAMES, SHOW_PROMOTIONS_PAGE } from "./isAllowedFeature";
import ReportsOverviewPage from "src/pages/Reports/ReportsOverviewPage";
import SalesReportPage from "src/pages/Reports/SalesReportPage";
import MenuPerformanceReportPage from "src/pages/Reports/MenuPerformanceReportPage";
import PaymentSummaryReportPage from "src/pages/Reports/PaymentSummaryReportPage";
import TaxSummaryReportPage from "src/pages/Reports/TaxSummaryReportPage";
import EmployeesReportPage from "../pages/Reports/EmployeesReportPage";
import { CashDrawerPage } from "src/features/CashDrawer/CashDrawerPage";
import {
  FinanceDisputesPage,
  FinanceOverviewPage,
  FinancePciCompliancePage,
  FinancePayoutsPage,
  FinanceTransactionsPage,
  PayrollPage,
} from "src/features/Finance";
import CategoryDetailPage from "src/features/MenuCategories/pages/CategoryDetailPage";
import MenuItemsListPage from "src/features/MenuCategories/pages/MenuItemsListPage";
import RestaurantGuidePage from "../pages/RestaurantGuidePage";
import QuickServiceSetupPage from "src/pages/QuickServiceSetupPage";

function RedirectToOrdersTab({ tab }: { tab: "active" | "completed" }) {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/orders?tab=${tab}`} replace />;
}

function RedirectReportsSalesSummary() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/reports/sales`} replace />;
}

function RedirectFinanceToTransactions() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/finance/overview`} replace />;
}

function RedirectPaymentsToFinancePayments() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/finance/payments`} replace />;
}

function RedirectFinanceTransactionsToPayments() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/finance/payments`} replace />;
}

function RedirectFinancePaymentAccountToSettings() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/restaurant-setting?tab=paymentAccount`} replace />;
}

function RedirectPaymentsCashDrawerToFinanceCashDrawer() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/finance/cash-drawer`} replace />;
}

function RedirectMenuIdToCategory() {
  const { restaurantSlugAndId, id } = useParams<{ restaurantSlugAndId: string; id: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/menu/categories/${id}`} replace />;
}

/** Legacy: redirect old my-restaurant/:id URLs to /restaurants/:segment/... Preserves path after restaurant id and search. */
function LegacyRestaurantRedirect() {
  const { restaurantCorrelationId } = useParams<{ restaurantCorrelationId: string }>();
  const location = useLocation();
  const segment = restaurantSegment(restaurantCorrelationId ?? "");
  // pathname is like /my-restaurant/uuid/menu/categories/xyz -> take segments after index 2
  const parts = location.pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("my-restaurant");
  const subpath = idx >= 0 && parts.length > idx + 2 ? parts.slice(idx + 2).join("/") : "";
  const to = subpath ? `/restaurants/${segment}/${subpath}${location.search}` : `/restaurants/${segment}${location.search}`;
  return <Navigate to={to} replace />;
}

/** When promotions are disabled, old bookmarks to my-restaurant/.../promotions go to Orders. */
function LegacyPromotionsDisabledRedirect() {
  const { restaurantCorrelationId } = useParams<{ restaurantCorrelationId: string }>();
  const segment = restaurantSegment(restaurantCorrelationId ?? "");
  return <Navigate to={`/restaurants/${segment}/orders`} replace />;
}

function RedirectPromotionsWhenDisabled() {
  const { restaurantSlugAndId } = useParams<{ restaurantSlugAndId: string }>();
  const { correlationId } = parseRestaurantSegment(restaurantSlugAndId ?? "");
  if (!correlationId) return <Navigate to="/my-restaurants" replace />;
  return <Navigate to={`/restaurants/${restaurantSegment(correlationId)}/orders`} replace />;
}

interface Children {
  path: string;
  element?: ReactNode;
  isRoleBase?: boolean;
  children?: RouteInterface[];
  roleName?: string;
}
export interface RouteInterface {
  path: string;
  element: ReactNode; // ReactNode can be used for JSX elements
  children?: Children[]; // Nested routes
}

const routesForAuthenticatedUser: RouteInterface[] = [
  {
    path: "/",
    element: <DefaultLayout />,
    children: [
      {
        path: "restaurants/:restaurantSlugAndId/combo/:id",
        element: <UpdateCombo />,
        isRoleBase: false,
      },
      {
        path: "my-restaurants",
        element: <RestaurantPage />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/restaurant-setting",
        element: <RestaurantSetting />,
        isRoleBase: false,
      },
      {
        path: "create-restaurant",
        element: <CreateRestaurant />,
        isRoleBase: false,
      },
      {
        path: "upload-image",
        element: <UploadImageRestaurant />,
        isRoleBase: false,
      },
      {
        path: "plans",
        element: <Plans />,
        isRoleBase: false,
      },
      {
        path: "stripe-connection",
        element: <Plans />,
        isRoleBase: false,
      },
      {
        path: "cancel",
        element: <Cancel />,
        isRoleBase: false,
      },
      {
        path: "success",
        element: <Success />,
        isRoleBase: false,
      },
      {
        path: "connectedAccount/refresh/:connectedAccountId",
        isRoleBase: false,
        element: <Refresh />,
      },
      {
        path: "connectedAccount/return/:connectedAccountId",
        isRoleBase: false,
        element: <Return />,
      },
      {
        path: "finix/onboarding/refresh/:storeCorrelationId",
        isRoleBase: false,
        element: <FinixOnboardingRefresh />,
      },
      {
        path: "finix/onboarding/return/:storeCorrelationId",
        isRoleBase: false,
        element: <FinixOnboardingReturn />,
      },
      {
        path: "qr-code",
        element: <QrCodeModal />,
        isRoleBase: false,
      },
      {
        path: "delete-restaurant",
        element: <DeleteRestaurant />,
        isRoleBase: false,
      },
      {
        path: "show-token-refresh-modal",
        element: <ShowTokenRefreshModal />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/employee",
        element: <Employee />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/promotions",
        element: SHOW_PROMOTIONS_PAGE ? (
          <RequireFeature feature={FEATURE_NAMES.PromotionManagement}>
            <Promotions />
          </RequireFeature>
        ) : (
          <RedirectPromotionsWhenDisabled />
        ),
        isRoleBase: SHOW_PROMOTIONS_PAGE,
        roleName: SHOW_PROMOTIONS_PAGE ? FEATURE_NAMES.PromotionManagement : undefined,
      },
      {
        path: "restaurants/:restaurantSlugAndId/orders",
        element: <OrdersPage />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/ordermanagement",
        element: <RedirectToOrdersTab tab="completed" />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/active-orders",
        element: <RedirectToOrdersTab tab="active" />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/restaurant-table",
        element: <RestaurantTable />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/customers",
        element: <CustomersPage />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/menu",
        element: <MenuCategoriesPage />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.MenuCreationAndManagement,
      },
      {
        path: "restaurants/:restaurantSlugAndId/menu/categories/:categoryId",
        element: <CategoryDetailPage />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/menu/items",
        element: <MenuItemsListPage />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.MenuCreationAndManagement,
      },
      {
        path: "restaurants/:restaurantSlugAndId/menu/:id",
        element: <RedirectMenuIdToCategory />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/modifier",
        element: <ModifierCategoriesPage />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.MenuCreationAndManagement,
      },
      {
        path: "restaurants/:restaurantSlugAndId/modifier/:modifierCategoryCorrelationId",
        element: <ModifierItemsPage />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/events",
        element: (
          <RequireFeature feature={FEATURE_NAMES.SpecialEventCreation}>
            <EventsPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.SpecialEventCreation,
      },
      {
        path: "restaurants/:restaurantSlugAndId/combo/:id",
        element: <UpdateCombo />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/combo",
        element: <Combo />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.ComboCreationAndManagement,
      },
      {
        path: "restaurants/:restaurantSlugAndId/quick-service",
        element: <QuickServiceSetupPage />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.MenuCreationAndManagement,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <ReportsOverviewPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/overview",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <ReportsOverviewPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/sales",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <SalesReportPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/sales-summary",
        element: <RedirectReportsSalesSummary />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/items",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <MenuPerformanceReportPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/payments",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <PaymentSummaryReportPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/employees",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <Suspense fallback={null}>
              <EmployeesReportPage />
            </Suspense>
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/reports/tax",
        element: (
          <RequireFeature feature={FEATURE_NAMES.ReportingAndAnalytics}>
            <TaxSummaryReportPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.ReportingAndAnalytics,
      },
      {
        path: "restaurants/:restaurantSlugAndId/guide",
        element: <RestaurantGuidePage />,
        isRoleBase: false,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/overview",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <FinanceOverviewPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/payments",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <FinanceTransactionsPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/payouts",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <FinancePayoutsPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/disputes",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <FinanceDisputesPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/transactions",
        element: <RedirectFinanceTransactionsToPayments />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/pci-compliance",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <FinancePciCompliancePage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/payment-account",
        element: <RedirectFinancePaymentAccountToSettings />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/cash-drawer",
        element: (
          <RequireFeature feature={FEATURE_NAMES.PaymentProcessingThroughStripe}>
            <CashDrawerPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance/payroll",
        element: (
          <RequireFeature feature={FEATURE_NAMES.EmployeePayroll}>
            <PayrollPage />
          </RequireFeature>
        ),
        isRoleBase: true,
        roleName: FEATURE_NAMES.EmployeePayroll,
      },
      {
        path: "restaurants/:restaurantSlugAndId/finance",
        element: <RedirectFinanceToTransactions />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/payments/cash-drawer",
        element: <RedirectPaymentsCashDrawerToFinanceCashDrawer />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      {
        path: "restaurants/:restaurantSlugAndId/payments",
        element: <RedirectPaymentsToFinancePayments />,
        isRoleBase: true,
        roleName: FEATURE_NAMES.PaymentProcessingThroughStripe,
      },
      // Legacy redirects: old my-restaurant/:id URLs -> /restaurants/:segment/...
      { path: "my-restaurant/:restaurantCorrelationId/restaurant-setting", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/employee", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      {
        path: "my-restaurant/:restaurantCorrelationId/promotions",
        element: SHOW_PROMOTIONS_PAGE ? <LegacyRestaurantRedirect /> : <LegacyPromotionsDisabledRedirect />,
        isRoleBase: false,
      },
      { path: "my-restaurant/:restaurantCorrelationId/orders", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/ordermanagement", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/active-orders", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/restaurant-table", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/customers", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/menu", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/menu/categories/:categoryId", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/menu/items", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/menu/:id", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/modifier", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/modifier/:modifierCategoryCorrelationId", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/events", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/combo/:id", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/combo", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/overview", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/sales", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/sales-summary", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/items", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/payments", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/employees", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/reports/tax", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/guide", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/finance/transactions", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/finance/cash-drawer", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/finance/payroll", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/finance", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/payments/cash-drawer", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      { path: "my-restaurant/:restaurantCorrelationId/payments", element: <LegacyRestaurantRedirect />, isRoleBase: false },
      {
        path: "invite-employee",
        element: <InviteEmployee />,
        isRoleBase: false,
      },
      {
        path: "recent-oreders",
        element: <Navigate to="/my-restaurants" replace />,
        isRoleBase: false,
      },
      {
        path: "user-profile-account",
        element: <UserProfileAccount />,
        isRoleBase: false,
      },
      {
        path: "subcription-setting",
        isRoleBase: false,
        element: <SubcriptionSetting />,
      },
    ],
  },
];

export default routesForAuthenticatedUser;
